import type { z } from 'zod'

import { FloorplanSchemaVersion } from '../constants/venueLayout'
import { VenueVisibilityValue } from '../constants/venue'
import {
  floorplanDocumentSchema,
  floorplanImageDocumentSchema,
  venueLayoutSchema,
} from '../schemas/venueLayout'
import { imgixImageUrl } from './urlUtils'

type VenueLayout = z.infer<typeof venueLayoutSchema>
type FloorplanDocument = z.infer<typeof floorplanDocumentSchema>

export type FloorplanImageDocument = z.infer<
  typeof floorplanImageDocumentSchema
>

export function isFloorplanImageDocument(
  doc: unknown
): doc is FloorplanImageDocument {
  return floorplanImageDocumentSchema.safeParse(doc).success
}

export function isFloorplanV3Document(doc: unknown): doc is FloorplanDocument {
  if (isFloorplanImageDocument(doc)) return false
  return floorplanDocumentSchema.safeParse(doc).success
}

type LayoutWithV3 = VenueLayout & {
  floorplanV3Draft?: unknown
  floorplanV3Published?: unknown
  floorplanV3Version?: number | null
}

export function layoutHasV3Floorplan(layout: VenueLayout): boolean {
  const row = layout as LayoutWithV3
  if (row.floorplanV3Draft) return true
  if (row.floorplanV3Published) return true
  return (row.floorplanV3Version ?? 0) > 0
}

type LayoutWithLegacyStorage = VenueLayout & {
  floorplanDraft?: unknown
  floorplanPublished?: unknown
}

export function layoutHasV3FloorplanInLegacyStorage(
  layout: VenueLayout
): boolean {
  const row = layout as LayoutWithLegacyStorage
  if (isFloorplanV3Document(row.floorplanDraft)) return true
  if (isFloorplanV3Document(row.floorplanPublished)) return true
  return false
}

export function layoutHasAnyV3Floorplan(layout: VenueLayout): boolean {
  return (
    layoutHasV3Floorplan(layout) || layoutHasV3FloorplanInLegacyStorage(layout)
  )
}

export function getIsLayoutPublic(
  visibility?: VenueVisibilityValue | null
): boolean {
  return visibility == null || visibility >= VenueVisibilityValue.PUBLIC
}

function hasLegacyFloorplanImage(layout: VenueLayout): boolean {
  return Boolean(
    layout.floorPlan?.large ||
      layout.floorPlan?.medium ||
      layout.floorPlan?.small ||
      layout.floorPlan?.xlarge ||
      layout.floorPlan?.filename
  )
}

/**
 * Live floorplan for venue maps. Matches the floorplans list: a layout is a
 * draft until a V2/V3 document is published, or it is a legacy image layout.
 */
export function isVenueLayoutPublished(layout: VenueLayout): boolean {
  if (
    layoutHasLiveV3Published(layout) ||
    layoutHasLiveV2ImagePublished(layout)
  ) {
    return true
  }
  if (!layoutHasV3Floorplan(layout) && !layoutHasV2ImageFloorplan(layout)) {
    return hasLegacyFloorplanImage(layout)
  }
  return false
}

/** Live on the customer map (not draft-only / unpublished). */
export function layoutHasLiveV3Published(layout: VenueLayout): boolean {
  if (!getIsLayoutPublic(layout.visibility)) return false
  const row = layout as LayoutWithV3
  return Boolean(row.floorplanV3Published)
}

export function layoutHasLiveV2ImagePublished(layout: VenueLayout): boolean {
  if (!getIsLayoutPublic(layout.visibility)) return false
  return Boolean(
    layout.floorplanPublished &&
      isFloorplanImageDocument(layout.floorplanPublished)
  )
}

function normalizeDate(
  value: Date | string | number | null | undefined
): Date | null {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function isSameUtcDay(a: Date, b: Date): boolean {
  return (
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate()
  )
}

function normalizeToUtcDay(
  value: Date | string | number | null | undefined
): Date | null {
  const date = normalizeDate(value)
  if (!date) return null
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  )
}

export function getDefaultVenueLayoutAvailability(): NonNullable<
  VenueLayout['availability']
> {
  return {
    days: [],
  }
}

export function layoutIsActiveOnDate(
  layout: Pick<VenueLayout, 'availability'>,
  value: Date | string | number
): boolean {
  const targetDate = normalizeToUtcDay(value)
  if (!targetDate) return true

  const availability =
    layout.availability ?? getDefaultVenueLayoutAvailability()
  if (
    availability.days?.length &&
    !availability.days.includes(targetDate.getUTCDay())
  ) {
    return false
  }

  const startDate = normalizeToUtcDay(availability.startDate)
  if (startDate && targetDate < startDate) {
    return false
  }

  const endDate = normalizeToUtcDay(availability.endDate)
  if (endDate && targetDate > endDate) {
    return false
  }

  if (
    availability.blackoutDates?.some((blackoutDate) => {
      const blackoutDay = normalizeToUtcDay(blackoutDate)
      return blackoutDay ? isSameUtcDay(blackoutDay, targetDate) : false
    })
  ) {
    return false
  }

  return true
}

export function layoutHasV2ImageFloorplan(layout: VenueLayout): boolean {
  if (
    layout.floorplanDraft &&
    isFloorplanImageDocument(layout.floorplanDraft)
  ) {
    return true
  }
  if (
    layout.floorplanPublished &&
    isFloorplanImageDocument(layout.floorplanPublished)
  ) {
    return true
  }
  return (layout.floorplanVersion ?? 0) > 0 && !layoutHasAnyV3Floorplan(layout)
}

export function resolveLayoutFloorplanSchemaVersion(
  layout: VenueLayout
): FloorplanSchemaVersion {
  if (layoutHasAnyV3Floorplan(layout)) return FloorplanSchemaVersion.V3
  if (layoutHasV2ImageFloorplan(layout)) return FloorplanSchemaVersion.V2
  return FloorplanSchemaVersion.LEGACY
}

export function layoutMatchesFloorplanSchemaVersions(
  layout: VenueLayout,
  allowed: FloorplanSchemaVersion[]
): boolean {
  return allowed.includes(resolveLayoutFloorplanSchemaVersion(layout))
}

export type VenueLayoutFloorplanMode = 'legacy' | 'v2'

export function resolvePublishedImageDocument(
  layout: VenueLayout
): FloorplanImageDocument | null {
  const published = layout.floorplanPublished
  if (!published || !isFloorplanImageDocument(published)) return null
  const parsed = floorplanImageDocumentSchema.safeParse(published)
  return parsed.success ? parsed.data : null
}

export function resolveVenueLayoutFloorplanMode(
  layout: VenueLayout
): VenueLayoutFloorplanMode {
  return resolvePublishedImageDocument(layout) ? 'v2' : 'legacy'
}

export function resolveVenueLayoutFloorplanImageUrl(
  layout: VenueLayout,
  opts?: { width?: number; variant?: 'medium' | 'large' }
): string | null {
  const published = resolvePublishedImageDocument(layout)
  const publishedOriginal = published?.floorPlan?.original
  if (publishedOriginal) {
    const cleanUrl = publishedOriginal.split('?')[0]
    return opts?.width
      ? imgixImageUrl(cleanUrl, { width: opts.width, fit: 'max' })
      : cleanUrl
  }

  const variant = opts?.variant ?? 'medium'
  return layout.floorPlan?.[variant] ?? null
}

export function resolveLayoutFloorPlanImageUrl(
  layout: VenueLayout
): string | null {
  if (
    layout.floorplanDraft &&
    isFloorplanImageDocument(layout.floorplanDraft)
  ) {
    return layout.floorplanDraft.floorPlan.original
  }

  if (
    layout.floorplanPublished &&
    isFloorplanImageDocument(layout.floorplanPublished)
  ) {
    return layout.floorplanPublished.floorPlan.original
  }

  if (!layout.floorPlan) return null

  const legacyOriginal = (
    layout.floorPlan as VenueLayout['floorPlan'] & {
      original?: string | null
    }
  )?.original

  return (
    layout.floorPlan.large ??
    layout.floorPlan.medium ??
    layout.floorPlan.small ??
    layout.floorPlan.xlarge ??
    layout.floorPlan.filename ??
    legacyOriginal ??
    null
  )
}
